import "./App.css";
import Page from "./app/dashboard/page";
import { Toaster } from "./components/ui/toaster";
import LoginPage from "./components/login";
import LandingPage from "./components/landing";
import { useAuth } from "./hooks/useAuth";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import RegisterPage from "./components/register";
import { Route, Switch, useLocation } from "wouter";
import ForgotPasswordPage from "./components/forgotpassword";
import ResetPasswordPage from "./components/resetpassword";
import SelfServicePage from "./Pages/SelfServicePage";
import SelfServiceDocumentPage from "./Pages/SelfServiceDocumentPage";
import VerifyEmail from "./components/verifyEmail";
import EmailNotVerified from "./components/emailNotVerified";
import RegisterInvitedMember from "./components/registerInvitedMember";
import SelfServiceOcrPage from "./Pages/SelfServiceOcrPage";
import SelfServiceOcrDocumentPage from "./Pages/SelfServiceOcrDocumentPage";
import SelfServiceCheckStatusPage from "./Pages/SelfServiceCheckStatusPage";
import PrivacyPolicyPage from "./Pages/PrivacyPolicyPage";
import { LightModeWrapper } from "./components/LightModeWrapper";


function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const [location] = useLocation();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const featureOcr = import.meta.env.VITE_FEATURE_OCR === "1";

  const queryClient = useQueryClient();

  useEffect(() => {
    const handleAuthError = () => {
      queryClient.invalidateQueries({ queryKey: ["refresh"] });
    };

    window.addEventListener("auth-error", handleAuthError);
    return () => window.removeEventListener("auth-error", handleAuthError);
  }, [queryClient]);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // Served regardless of auth state, and without waiting for the auth check,
  // so the policy stays reachable from emails, contracts and the T&C text.
  if (location === "/privacy-policy") {
    return <PrivacyPolicyPage />;
  }

  if (isInitialLoading) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground animate-pulse">
            Loading your application...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated ? (
        <>
          <Page />
          <Toaster />
        </>
      ) : (
        <LightModeWrapper>
          <Switch>
            <Route path="/login" component={LoginPage} />
            <Route path="/register" component={RegisterPage} />
            <Route path="/forgot-password" component={ForgotPasswordPage} />
            <Route path="/resetpassword" component={ResetPasswordPage} />
            <Route path="/" component={LandingPage} />
            <Route path="/self-service" component={SelfServicePage} />
            <Route path="/self-service/check" component={SelfServiceCheckStatusPage} />
            <Route path="/self-service/:id" component={SelfServiceDocumentPage} />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route path="/email-not-verified" component={EmailNotVerified} />
            <Route path="/register-invited-member" component={RegisterInvitedMember} />
            {featureOcr && (
              <>
                <Route path="/self-service-ocr" component={SelfServiceOcrPage} />
                <Route path="/self-service-ocr-document" component={SelfServiceOcrDocumentPage} />
              </>
            )}
          </Switch>
          <Toaster />
        </LightModeWrapper>
      )}
    </>
  );
}

export default App;
